import { useAuth } from 'src/auth/AuthContext';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  CircularProgress
} from '@mui/material';
import { Check } from '@mui/icons-material';
import LocationRegistrationForm from '../../components/CreateLocationForm';
import SubscriptionAdvertisement from '../../components/SubscriptionAd';
import AlternativeSubscriptionAdvertisement from '../../components/AlternativeSubscriptionAd';
import { useNavigate } from 'react-router-dom';
import { initiatePayfastCheckout } from '../../utils/payfast';
import { useLocation } from 'react-router-dom';
import { usePricing } from '../../hooks/usePricing';
import { useIsMutating } from '@tanstack/react-query';
import { keys } from 'src/queries/keys';
import { Merchant } from 'src/interfaces/interfaces';

const CreateLocationPage: React.FC = () => {
  const user = useAuth();
  /**
   * The location created by the form on the previous step. It used to live in
   * the merchant slice, which meant a half-finished registration was persisted
   * to localStorage and could reappear on a later visit. Holding it here scopes
   * it to this wizard.
   */
  const [createdLocation, setCreatedLocation] = useState<Merchant | null>(null);

  // The footer button lives outside the form it submits, so it watches the
  // create mutation directly rather than the form reporting upwards.
  const isCreating = useIsMutating({ mutationKey: keys.locations.create }) > 0;
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const [formIsValid, setFormIsValid] = useState(false);
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [validateFormFn, setValidateFormFn] = useState<(() => void) | null>(null);
  const [showFormError, setShowFormError] = useState(false);
  const [useAlternativeFlow, setUseAlternativeFlow] = useState(false);
  const [currentReferralCode, setCurrentReferralCode] = useState('');

  // Free-plan countries (no CountryPricing row on the backend) skip the
  // subscription step entirely; priced countries go through the paid PayFast
  // flow. Resolved from the pricing endpoint using the address of the location
  // that was just created. Defaults to paid until pricing is known.
  const { pricing, loading: pricingLoading } = usePricing(
    createdLocation?.address?.countryCode
  );
  const requiresPaidSubscription = pricing?.is_free !== true;

  const stepParam = new URLSearchParams(location.search).get('step');
  if (stepParam) {
    if (parseInt(stepParam) !== activeStep)
    setActiveStep(parseInt(stepParam));
  }

  // These callbacks are passed down into Formik monitor components whose
  // effects depend on them; stable identities are required or the
  // callback-prop → effect → parent-setState chain re-renders forever.
  const handleFormStateChange = useCallback((isValid: boolean, isDirty: boolean) => {
    setFormIsValid(isValid);
    setFormIsDirty(isDirty);

    // Hide error message if form becomes valid
    if (isValid) {
      setShowFormError(false);
    }
  }, []);

  const handleValidateForm = useCallback((validateFn: () => void) => {
    setValidateFormFn(() => validateFn);
  }, []);

  // Watch for form validity changes to hide error message
  useEffect(() => {
    if (formIsValid && showFormError) {
      setShowFormError(false);
    }
  }, [formIsValid, showFormError]);

  // Check for alternative subscription flow flag when component mounts or step changes
  useEffect(() => {
    if (activeStep === 1) {
      const alternativeFlag = sessionStorage.getItem('alternativeSubscriptionFlow');
      if (alternativeFlag === 'true') {
        setUseAlternativeFlow(true);
        // Clear the flag after using it
        sessionStorage.removeItem('alternativeSubscriptionFlow');
      }
    }
  }, [activeStep]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePaymentRedirect = async () => {
    // The backend derives the amount from the merchant's country and type;
    // we only say what we want to do.
    await initiatePayfastCheckout({
      merchantId: createdLocation?.id ?? -1,
      intent: shouldUseAlternativeFlow() ? 'billImmediately' : 'subscribe',
      referralCode: currentReferralCode || undefined
    });
  };

  // Step list is country-dependent: paid merchants see the Activate
  // Subscription step; free-plan merchants jump straight from profile
  // creation to the completion screen.
  const steps = requiresPaidSubscription
    ? ['Location profile', 'Subscription', 'Complete']
    : ['Location profile', 'Complete'];

  const completeStepIndex = requiresPaidSubscription ? 2 : 1;

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleFinish = () => {
    // Open the freshly created location, falling back to the locations list.
    if (createdLocation && createdLocation.id > 0) {
      navigate(`/locations/${createdLocation.id}`);
    } else {
      navigate('/');
    }
  };

  const handleReferralCodeChange = useCallback((referralCode: string) => {
    setCurrentReferralCode(referralCode);
  }, []);

  // Check if we should use alternative flow based on referral code
  const shouldUseAlternativeFlow = () => {
    return useAlternativeFlow || currentReferralCode.trim().toLowerCase() === 'chris green';
  };

  const headingFont = "'Bricolage Grotesque', sans-serif";
  const bodyFont = "'Instrument Sans', system-ui, sans-serif";

  return (
    <Box
      sx={{
        maxWidth: 720,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px'
      }}
    >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography
              component='h1'
              sx={{
                fontFamily: headingFont,
                fontWeight: 700,
                fontSize: 36,
                lineHeight: 1.05,
                letterSpacing: '-.02em',
                color: '#17222C'
              }}
            >
              Welcome, {user.firstName}
            </Typography>
            <Typography sx={{ fontFamily: bodyFont, fontSize: 16, color: '#5F6B76' }}>
              Let's set up your location profile to get you started.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
              gap: '8px'
            }}
          >
            {steps.map((label, index) => {
              const reached = index <= activeStep;
              return (
                <Box key={label} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: '4px',
                      backgroundColor: reached ? '#1778A8' : '#D7E1E8'
                    }}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'baseline',
                      fontFamily: bodyFont,
                      fontWeight: 500,
                      fontSize: 14,
                      color: reached ? '#17222C' : '#7C8893'
                    }}
                  >
                    <span>{index + 1}</span>
                    <span>{label}</span>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {activeStep === 0 && (
            <>
              {!formIsDirty && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Please fill out all required fields to create your location profile.
                </Alert>
              )}
              <LocationRegistrationForm
                onComplete={(created) => {
                  setCreatedLocation(created);
                  handleNext();
                }}
                onFormStateChange={handleFormStateChange}
                validateForm={handleValidateForm}
                isCoffeeShop={true}
                onReferralCodeChange={handleReferralCodeChange}
              />
              {showFormError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Please fix all highlighted fields before submitting the form. The form contains errors or missing required information.
                </Alert>
              )}
            </>
          )}

          {activeStep === 1 && requiresPaidSubscription && (
            pricingLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                {shouldUseAlternativeFlow() ? (
                  <AlternativeSubscriptionAdvertisement
                    onProceed={handleClickOpen}
                    countryCode={createdLocation?.address?.countryCode}
                    typeName={createdLocation?.type?.name}
                  />
                ) : (
                  <SubscriptionAdvertisement
                    onProceed={handleClickOpen}
                    referralCode={currentReferralCode}
                    countryCode={createdLocation?.address?.countryCode}
                    typeName={createdLocation?.type?.name}
                  />
                )}
              </Box>
            )
          )}

          {activeStep === completeStepIndex && (
            <Box
              sx={{
                backgroundColor: '#fff',
                border: '1px solid #E6ECF0',
                borderRadius: '16px',
                p: '40px 32px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: '16px'
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: '#E3F1E6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Check sx={{ fontSize: 28, color: '#1F6B3A' }} />
              </Box>
              <Typography
                sx={{
                  fontFamily: headingFont,
                  fontWeight: 700,
                  fontSize: 26,
                  lineHeight: 1.1,
                  color: '#17222C'
                }}
              >
                Your location is live
              </Typography>
              <Typography
                sx={{
                  fontFamily: bodyFont,
                  fontSize: 15,
                  lineHeight: 1.45,
                  color: '#5F6B76',
                  maxWidth: 420
                }}
              >
                {requiresPaidSubscription
                  ? 'Customers can now collect stamps at this location.'
                  : "Customers can now collect stamps at this location. You're on our free plan — no payment required."}
              </Typography>
              <Button
                variant='contained'
                onClick={handleFinish}
                sx={{
                  height: 46,
                  px: '20px',
                  mt: '8px',
                  backgroundColor: '#1778A8',
                  fontFamily: bodyFont,
                  fontSize: 15,
                  '&:hover': { backgroundColor: '#0E4F70' }
                }}
              >
                Go to location
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            {activeStep === 0 ? (
              <Button
                form={formIsValid && formIsDirty && !isCreating ? 'merchant-submit-form' : undefined}
                variant='contained'
                color='primary'
                type={formIsValid && formIsDirty && !isCreating ? 'submit' : 'button'}
                sx={{
                  opacity: (!formIsValid || !formIsDirty || isCreating) ? 0.5 : 1,
                  backgroundColor: (!formIsValid || !formIsDirty || isCreating) ? 'rgba(0, 0, 0, 0.12)' : undefined,
                  color: (!formIsValid || !formIsDirty || isCreating) ? 'rgba(0, 0, 0, 0.26)' : undefined,
                  boxShadow: (!formIsValid || !formIsDirty || isCreating) ? 'none' : undefined,
                  '&:hover': {
                    opacity: (!formIsValid || !formIsDirty || isCreating) ? 0.5 : 0.9,
                    backgroundColor: (!formIsValid || !formIsDirty || isCreating) ? 'rgba(0, 0, 0, 0.12)' : undefined,
                    boxShadow: (!formIsValid || !formIsDirty || isCreating) ? 'none' : undefined
                  }
                }}
                onClick={() => {
                  // If form is valid and dirty, check for Chris Green referral code
                  if (formIsValid && formIsDirty && !isCreating) {
                    if (currentReferralCode.trim().toLowerCase() === 'chris green') {
                      const form = document.getElementById('merchant-submit-form') as HTMLFormElement;
                      if (form) {
                        form.requestSubmit();
                      }
                      return;
                    }
                  }

                  // If form is not valid, trigger validation
                  if (!formIsValid && validateFormFn) {
                    validateFormFn();
                    setShowFormError(true);
                  }
                }}
                onContextMenu={(event) => {
                  // Right-click → trigger the alternative subscription flow
                  // (only meaningful for paid merchants who hit the PayFast step).
                  event.preventDefault();
                  if (formIsValid && formIsDirty && !isCreating) {
                    sessionStorage.setItem('alternativeSubscriptionFlow', 'true');
                    const form = document.getElementById('merchant-submit-form') as HTMLFormElement;
                    if (form) {
                      form.requestSubmit();
                    }
                  }
                }}
              >
                Create Location
              </Button>
            ) : null}
          </Box>

        <Dialog open={open} onClose={handleClose}>
          <DialogTitle>Payment Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText>
              You are about to be redirected to our secure payment provider's
              portal. Do you wish to proceed?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color='primary'>
              Cancel
            </Button>
            <Button onClick={handlePaymentRedirect} color='primary'>
              Proceed to Payment
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
};

export default CreateLocationPage;
